import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, User, Heart, Activity, Target, Sparkles, Play } from "lucide-react";

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import YouTubeFacade from "@/components/YouTubeFacade";

// YouTube video used for the "How it works" walk-through. Kept in one
// place so a future asset swap only touches this file.
const YT_ID = "BodoPoqBf68";

// The 4 onboarding steps rendered below the video with a staggered
// entrance animation. Icons + accent hues echo the existing STEPS array
// in Landing.jsx so the popup feels visually continuous with the
// on-page "How It Works" section.
const STEPS = [
  { n: "01", icon: User,     title: "Create Account",      body: "Sign up free in under 60 seconds.",                     accent: "from-coral",       haloBg: "bg-coral/10",      iconColor: "text-coral" },
  { n: "02", icon: Heart,    title: "Add Child Profile",   body: "Quick guided wizard — age, concerns, strengths.",       accent: "from-emerald-500", haloBg: "bg-emerald-100",   iconColor: "text-emerald-500" },
  { n: "03", icon: Activity, title: "Complete Assessment", body: "8-domain developmental screen, friendly scale.",        accent: "from-violet-500",  haloBg: "bg-violet-100",    iconColor: "text-violet-500" },
  { n: "04", icon: Target,   title: "Get Action Plan",     body: "Beautiful report + daily activities you can start today.", accent: "from-amber-500",   haloBg: "bg-amber-100",     iconColor: "text-amber-500" },
];

/**
 * HowItWorksDialog — video walk-through + 4-step animated diagram +
 * "Start Free Today" CTA. Triggered from the Landing hero's "See how it
 * works" button (added 2026-02). Uses the shared shadcn Dialog primitive
 * so the accessibility (focus trap, ESC-to-close, backdrop dismiss)
 * matches the rest of the site's popups.
 *
 * Props:
 *   open           : boolean         — controlled visibility
 *   onOpenChange   : (bool) => void  — controlled open/close
 */
export default function HowItWorksDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="how-it-works-dialog"
        className="p-0 overflow-hidden border-0 rounded-3xl max-w-3xl w-[calc(100vw-1.25rem)] sm:w-full max-h-[90vh] flex flex-col"
      >
        <div className="bg-gradient-to-br from-[#F0FDFA] to-white px-5 sm:px-8 pt-6 pb-4 border-b border-slate-100">
          <DialogHeader className="text-left">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <span className="inline-flex items-center gap-1 text-[10px] sm:text-[11px] uppercase tracking-[0.18em] font-bold text-[#2F6F6A]">
                <Play className="h-3 w-3 fill-current" /> How It Works
              </span>
            </div>
            <DialogTitle className="font-display text-xl sm:text-2xl font-extrabold text-[#1F2933]">
              From signup to action plan — in ~10 minutes.
            </DialogTitle>
            <DialogDescription className="text-[13px] sm:text-sm text-slate-600 mt-1">
              Watch the 2-minute walk-through, then follow the 4 guided steps to your child&apos;s personalised action plan.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-5 sm:px-8 py-5 overflow-y-auto space-y-6 bg-slate-50/40">
          {/* Walk-through video — facade pattern (see YouTubeFacade.jsx).
              Since the dialog itself is only mounted on user tap, we
              hydrate the facade immediately BUT keep the click-to-play
              interaction so the popup opens instantly instead of waiting
              for YouTube's ~500KB JS bundle. Full-HD hints are applied
              inside YouTubeFacade when the parent taps play. */}
          {open && (
            <YouTubeFacade
              videoId={YT_ID}
              title="How Kiddo+ works"
              testId="how-it-works-video"
              className="relative rounded-2xl overflow-hidden bg-black shadow-lg aspect-video"
              startMuted={true}
            />
          )}

          {/* 4-step animated diagram. Motion staggers the entrance so
              parents can visually track the flow rather than seeing all
              four cards appear at once. */}
          <div>
            <h4 className="font-display font-extrabold text-[#1F2933] text-base sm:text-lg">4 Simple Steps</h4>
            <p className="text-[13px] text-slate-500 mt-1">Guided, in-app — no clinical background required.</p>
            <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="how-it-works-steps">
              {STEPS.map((s, i) => (
                <motion.div
                  key={s.n}
                  initial={{ opacity: 0, y: 12 }}
                  animate={open ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
                  transition={{ duration: 0.45, delay: 0.18 + i * 0.12, ease: "easeOut" }}
                  className="relative bg-white rounded-2xl shadow-sm border border-plum/5 px-3 py-5 sm:py-6 flex flex-col items-center text-center overflow-hidden"
                  data-testid={`how-it-works-step-${s.n}`}
                >
                  <span className={`absolute top-0 left-4 right-4 h-1 rounded-b-full bg-gradient-to-r ${s.accent} to-transparent`} />
                  <span className="absolute top-3 right-4 text-[11px] font-bold tracking-widest text-inkmid/60">{s.n}</span>
                  <motion.div
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={open ? { scale: 1, opacity: 1 } : { scale: 0.5, opacity: 0 }}
                    transition={{ duration: 0.5, delay: 0.26 + i * 0.12, ease: "easeOut" }}
                    className={`w-14 h-14 rounded-full ${s.haloBg} flex items-center justify-center mb-3 mt-1`}
                  >
                    <s.icon className={`w-6 h-6 ${s.iconColor}`} strokeWidth={2.2} />
                  </motion.div>
                  <p className="font-heading text-sm sm:text-base leading-tight text-inkdark">{s.title}</p>
                  <p className="text-[11px] text-inkmid leading-snug mt-1.5">{s.body}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Bottom CTA — mirrors the on-page "How it works" section CTA
              so the funnel behaviour is consistent. */}
          <div className="rounded-2xl bg-[#2F6F6A] text-white p-5 sm:p-6 text-center">
            <p className="font-display font-extrabold text-lg sm:text-xl">Ready to see your child&apos;s plan?</p>
            <p className="text-[13px] text-white/85 mt-1">Free to start · No credit card required · Under 5 minutes</p>
            <Link
              to="/signup"
              onClick={() => onOpenChange?.(false)}
              className="mt-4 inline-flex items-center gap-2 bg-[#F4C430] hover:bg-[#fbd34c] text-[#1F2933] rounded-full px-6 py-3 font-extrabold text-sm sm:text-base shadow"
              data-testid="how-it-works-cta"
            >
              <Sparkles className="h-4 w-4" /> Start Free Today <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
